/**
 * Hostname resolution for landing pages.
 *
 * Three ways a landing page can be reached:
 *  1. App host (dropycash.com / preview / localhost) → /lp/:slug
 *  2. Seller store subdomain → storename.dropycash.com/:slug  (needs wildcard DNS+TLS)
 *  3. Verified seller custom domain → shop.example.ma/:slug
 *
 * The app resolves all three purely from the request hostname; the DNS and TLS
 * side of (2) and (3) is infrastructure (Cloudflare + Coolify) and is listed as
 * an external prerequisite.
 */

export const ROOT_DOMAIN = "dropycash.com";

/** Hosts that serve the DropyCash application itself, never a landing page. */
const APP_HOSTS = new Set([
  ROOT_DOMAIN,
  `www.${ROOT_DOMAIN}`,
  "localhost",
  "127.0.0.1",
]);

export type HostContext =
  | { kind: "app" }
  | { kind: "store"; subdomain: string }
  | { kind: "custom"; hostname: string };

export function resolveHostContext(hostname: string = typeof window !== "undefined" ? window.location.hostname : ""): HostContext {
  const host = (hostname || "").toLowerCase().replace(/:\d+$/, "");
  if (!host) return { kind: "app" };
  if (APP_HOSTS.has(host)) return { kind: "app" };
  // Lovable preview / staging hosts behave like the app host.
  const PREVIEW_ZONES = [
    "lovable.app",
    "lovable.dev",
    "lovableproject.com",
    "lovableproject-dev.com",
    "gpt-eng.com",
    "gptengineer.run",
    "gptengineer.app",
    "sandbox.lovable.dev",
  ];
  if (PREVIEW_ZONES.some((z) => host === z || host.endsWith(`.${z}`))) return { kind: "app" };
  if (host.endsWith(`.${ROOT_DOMAIN}`)) {
    const sub = host.slice(0, -1 * (ROOT_DOMAIN.length + 1));
    if (!sub || sub.includes(".")) return { kind: "app" };
    return { kind: "store", subdomain: sub };
  }
  return { kind: "custom", hostname: host };
}

/** Canonical public URL of a page, given its store subdomain / custom domain. */
export function buildPublicUrl(opts: {
  slug: string;
  storeSubdomain?: string | null;
  customHostname?: string | null;
}): string {
  const { slug, storeSubdomain, customHostname } = opts;
  if (customHostname) return `https://${customHostname}/${slug}`;
  if (storeSubdomain) return `https://${storeSubdomain}.${ROOT_DOMAIN}/${slug}`;
  return `https://${ROOT_DOMAIN}/lp/${slug}`;
}

export function dnsInstructionsFor(hostname: string, token: string) {
  const isApex = hostname.split(".").length <= 2;
  return [
    {
      type: "TXT",
      name: `_dropycash.${hostname}`,
      value: `dropycash-verify=${token}`,
      purpose: "Prouve que vous contrôlez ce domaine.",
    },
    isApex
      ? {
          type: "A",
          name: "@",
          value: "PENDING_INFRA_IP",
          purpose: "Pointe le domaine vers l'hébergement DropyCash.",
        }
      : {
          type: "CNAME",
          name: hostname.split(".")[0],
          value: `lp.${ROOT_DOMAIN}`,
          purpose: "Pointe le sous-domaine vers l'hébergement DropyCash.",
        },
  ];
}
